import axios from 'axios';
import authService from './authService';
import { API_URL } from '../config/config';
import { logErrorToBackend } from './errorLogger';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Configuration globale d'axios
axios.defaults.baseURL = API_URL;

// Créer une instance axios distincte pour le refresh token qui n'a pas d'intercepteurs
export const axiosNoInterceptor = axios.create({
    baseURL: API_URL
});

axios.interceptors.request.use(
    async config => {
        
        // Ne pas intercepter les requêtes de refresh token
        if (config.url.includes('/token/refresh')) {
            return config;
        }
        
        const token = authService.getToken();
        if (!token) return config;

        // Vérifie si le token est expiré
        if (authService.isTokenExpired(token)) {
            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    const newToken = await authService.refreshToken();
                    isRefreshing = false;
                    processQueue(null, newToken);
                    config.headers.Authorization = `Bearer ${newToken}`;
                    return config;
                } catch (error) {
                    console.error('Token refresh failed in request interceptor', error);
                    isRefreshing = false;
                    processQueue(error, null);
                    authService.logout();
                    return Promise.reject(error);
                }
            }

            // Mettre la requête en attente
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                config.headers.Authorization = `Bearer ${token}`;
                return config;
            }).catch(err => {
                return Promise.reject(err);
            });
        }

        // Token valide, on l'ajoute à la requête
        config.headers.Authorization = `Bearer ${token}`;
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

axios.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        // Logger uniquement les erreurs critiques (5xx et network errors)
        // Ne pas logger les 401 qui sont gérés par le refresh token
        if (error.response) {
            const status = error.response.status;
            // Logger uniquement les erreurs serveur 5xx (pas les 401 qui sont normaux)
            if (status >= 500) {
                logErrorToBackend(
                    new Error(`Axios Interceptor Error ${status}: ${error.message}`),
                    {
                        type: 'axios_interceptor',
                        status: status,
                        url: error.config?.url,
                        method: error.config?.method?.toUpperCase(),
                        data: error.response.data
                    }
                );
            }
        } else if (error.request) {
            logErrorToBackend(
                new Error(`Axios Network Error: ${error.message}`),
                {
                    type: 'axios_network',
                    url: error.config?.url,
                    method: error.config?.method?.toUpperCase()
                }
            );
        }

        if (error.response?.status === 402) {
            // Paiement requis : bloquer l'accès et forcer la mise à jour du moyen de paiement
            window.dispatchEvent(new CustomEvent('payment_required', {
                detail: {
                    failed_payments: error.response.data?.failed_payments,
                    message: error.response.data?.message
                }
            }));
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // Vérifier si l'utilisateur a un token
            const token = authService.getToken();
            if (!token) {
                authService.logout();  // Nettoyer les données utilisateur
                window.location.href = '/login';  // Rediriger vers la page de connexion
                return Promise.reject(error);
            }

            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    const newToken = await authService.refreshToken();
                    isRefreshing = false;
                    processQueue(null, newToken);
                    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axios(originalRequest);
                } catch (refreshError) {
                    console.error('Token refresh failed in response interceptor', refreshError);
                    isRefreshing = false;
                    processQueue(refreshError, null);
                    authService.logout();
                    return Promise.reject(refreshError);
                }
            }

            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axios(originalRequest);
            }).catch(err => {
                return Promise.reject(err);
            });
        }

        return Promise.reject(error);
    }
);

// Export l'instance Axios configurée
export default axios;
